// ArenasHub.js
import React, { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import LoadingSpinner from "./LoadingSpinner";

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function ArenasHub({
  onNavigate,
  user,
  setUser,
  setSelectedCardId,
  arenaIndex: initialArenaIndex,
  arenaId: initialArenaId,
}) {
  const { arenas, loaded } = useAppConfig();

const arenasList = useMemo(() => {
  const list = Array.isArray(arenas?.arenas)
    ? arenas.arenas
    : Array.isArray(arenas)
    ? arenas
    : [];

  return list.filter((a) => a?.enabled !== false);
}, [arenas]);

  //useEffect(() => {
//    setSelectedCardId?.(null);
//  }, []);

  const [me, setMe] = useState(user ?? null);
  const [arenaState, setArenaState] = useState(null);

  useEffect(() => {
    if (user) setMe(user);
  }, [user]);

  const trophies = Number(me?.trophies ?? user?.trophies ?? 0);
  const hasGamepass = Boolean(me?.gamepass_active ?? user?.gamepass_active);
  const [unopenedChestCountByArena, setUnopenedChestCountByArena] = useState({});
  
  const scrollerRef = useRef(null);

  const [ownedArenaIds, setOwnedArenaIds] = useState(() => new Set());
  const [purchaseBusy, setPurchaseBusy] = useState(false);

  const [pageIdx, setPageIdx] = useState(0);

const getPageWidth = () => scrollerRef.current?.clientWidth || window.innerWidth;

const scrollToIndex = useCallback((idx) => {
  const el = scrollerRef.current;
  if (!el) return;

  const maxIdx = Math.max(0, arenasList.length - 1);
  const next = Math.max(0, Math.min(maxIdx, idx));

  el.scrollTo({
    left: next * getPageWidth(),
    behavior: "smooth",
  });

  setPageIdx(next);
}, [arenasList.length]);

useEffect(() => {
  if (!loaded) return;
  if (!arenasList.length) return;

  let targetIdx = -1;

  if (initialArenaId) {
    targetIdx = arenasList.findIndex(
      (a) => String(a?.id) === String(initialArenaId)
    );
  }

  if (targetIdx < 0 && Number.isInteger(initialArenaIndex)) {
    targetIdx = Math.max(0, Math.min(initialArenaIndex, arenasList.length - 1));
  }

  if (targetIdx < 0) return;

  setPageIdx(targetIdx);

  const el = scrollerRef.current;
  if (!el) return;

  const jump = () => {
    el.scrollLeft = targetIdx * getPageWidth();
  };

  // roda depois do layout montar
  requestAnimationFrame(jump);
}, [loaded, arenasList, initialArenaId, initialArenaIndex]);

// Update pageIdx when user swipes
useEffect(() => {
  const el = scrollerRef.current;
  if (!el) return;

  let raf = 0;

  const onScroll = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const w = getPageWidth();
      const idx = w ? Math.round(el.scrollLeft / w) : 0;
      setPageIdx(Math.max(0, Math.min(arenasList.length - 1, idx)));
    });
  };

  el.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  return () => {
    cancelAnimationFrame(raf);
    el.removeEventListener("scroll", onScroll);
  };
}, [arenasList.length]);

  async function fetchArenaState() {
  const token = localStorage.getItem("token");

  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/me`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to load arena state");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  return data;
}

async function fetchAllUnopenedChests() {
  const token = localStorage.getItem("token");

  //const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/chests/all`, {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/chests/all-by-arena`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to load unopened chests");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  return Array.isArray(data?.chests) ? data.chests : [];
}

const refreshUnopenedChestCounts = useCallback(async () => {
  try {
    const chests = await fetchAllUnopenedChests();

    const grouped = {};

    for (const chest of chests) {
  const arenaId = String(
    chest?.arenaId ??
    chest?.arena_id ??
    chest?.arena ??
    ""
  );

  if (!arenaId) continue;
  grouped[arenaId] = (grouped[arenaId] || 0) + 1;
}

    setUnopenedChestCountByArena(grouped);
  } catch (e) {
    console.warn("[arenasHub] refreshUnopenedChestCounts failed:", e?.message || e);
    setUnopenedChestCountByArena({});
  }
}, []);

useEffect(() => {
  refreshUnopenedChestCounts();
}, [refreshUnopenedChestCounts]);

async function fetchMe() {
  const token = localStorage.getItem("token");

  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to load user");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  return data;
}

const refreshMe = useCallback(async () => {
  try {
    const fresh = await fetchMe();
    const next = fresh?.user ?? fresh;

    if (next && typeof next === "object") {
      setMe(next);        // atualiza local
      setUser?.(next);    // atualiza global (igual Arenas.js)
    }

    const st = await fetchArenaState();
    setArenaState(st);

  } catch (e) {
    console.warn("[arenasHub] refreshMe failed:", e?.message || e);
  }
}, [setUser]);


// Refresh on mount
useEffect(() => {
  refreshMe();
}, [refreshMe]);

// Refresh when tab/app becomes active again
useEffect(() => {
  const onFocus = () => {
    refreshMe();
    refreshUnopenedChestCounts();
  };

  const onVis = () => {
    if (document.visibilityState === "visible") {
      refreshMe();
      refreshUnopenedChestCounts();
    }
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVis);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVis);
  };
}, [refreshMe, refreshUnopenedChestCounts]);

// pega arenas compradas do servidor (sua rota já existe)
async function fetchMyArenaEntries() {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/my-entries`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || "FAILED_TO_LOAD_MY_ENTRIES");

  // suporta {arenaIds:[...]} OU {items:[{arena_id}]} se você retornar diferente
  const arenaIds =
    Array.isArray(data?.arenaIds) ? data.arenaIds :
    Array.isArray(data?.items) ? data.items.map(x => x?.arena_id) :
    [];

  return new Set(arenaIds.map(String).filter(Boolean));
}

// carrega uma vez ao abrir a tela
useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const set = await fetchMyArenaEntries();
      if (!cancelled) setOwnedArenaIds(set);
    } catch (e) {
      console.log("my-entries failed:", e?.message);
    }
  })();

  return () => { cancelled = true; };
}, []);

const ownsArenaEntry = useCallback(
  (arenaId) => ownedArenaIds.has(String(arenaId)),
  [ownedArenaIds]
);

    async function purchaseArenaEntry(arenaId) {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/arenas/purchase-entry`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ arenaId }),
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch {
      // ignore
    }

    if (!res.ok) {
      const err = data?.error || "PURCHASE_FAILED";
      const msg =
        err === "NOT_ENOUGH_RUNES"
          ? "Not enough runes."
          : err === "NOT_ENOUGH_SPARKS"
          ? "Not enough sparks."
          : err === "ARENA_NOT_FOUND"
          ? "Arena not found."
          : "Purchase failed.";
      const e = new Error(msg);
      e.code = err;
      throw e;
    }

    return data;
  }

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingArena, setPendingArena] = useState(null);

  const isArenaOwned = useCallback(
    (a) => ownsArenaEntry(a?.id),
    [ownsArenaEntry]
  );

  const enterArena = useCallback(
  (a, opts = {}) => {
    const globalIdx = arenasList.findIndex((x) => x?.id === a?.id);
    if (globalIdx < 0) return;

    onNavigate?.("arenas", {
      arenaIndex: globalIdx,
      arenaId: a?.id,
      openedVia: "arenasHub",
      viewOnly: Boolean(opts.viewOnly),
    });
  },
  [arenasList, onNavigate]
);

  const isPaidArena = useCallback((a) => {
  if (!a) return false;
  return Boolean(
    a.isPaid ||
      a.paid ||
      a.type === "paid" ||
      a.access === "paid" ||
      (Number(a?.entryFee?.amount ?? 0) > 0) ||
      (Number(a?.entryCost ?? 0) > 0)
  );
}, []);


  const canEnterByTrophies = useCallback(
    (a) => {
      const min = toNum(a?.trophyMin, -Infinity);
      const max = toNum(a?.trophyMax, Infinity);
      return trophies >= min && trophies <= max;
    },
    [trophies]
  );

  const canEnterByPass = useCallback(
    (a) => {
      if (!isPaidArena(a)) return true;
      return hasGamepass;
    },
    [hasGamepass, isPaidArena]
  );

  const neededForMin = useCallback(
    (a) => Math.max(0, toNum(a?.trophyMin, 0) - trophies),
    [trophies]
  );

  const getArenaPriceLabel = useCallback((a) => {
  const amount = Number(a?.entryFee?.amount ?? 0);
  const currency = String(a?.entryFee?.currency ?? "free").toLowerCase();

  if (!Number.isFinite(amount) || amount <= 0 || currency === "free") return "FREE";
  if (currency === "runes") return `${amount} RUNES`;
  if (currency === "sparks") return `${amount} SPARKS`;
  return `${amount} ${currency.toUpperCase()}`;
}, []);

  const effectiveArenaId = String(arenaState?.effectiveArenaId || "");
const protectionActive = !!arenaState?.protection;

const currentIdx = useMemo(() => {
  if (!effectiveArenaId) return -1;
  return arenasList.findIndex(a => String(a?.id) === effectiveArenaId);
}, [arenasList, effectiveArenaId]);


if (!loaded) {
  return (
    <div
      style={{
        width: "100%",
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoadingSpinner text="Loading Arenas" size={64} />
    </div>
  );
}

  return (
    <div style={{width: "100%",aspectRatio: 0.75,float: "left"}}>
    
      <button
        type="button"
        onClick={() => scrollToIndex(pageIdx - 1)}
        disabled={pageIdx <= 0}
        style={{
          position: "absolute",
          top: "25%",
          left: 0,
          zIndex: 60,
          width: 34,
          height: 52,
          background: "transparent",
          display: "grid",
          border: 0,
          placeItems: "center",
          cursor: pageIdx <= 0 ? "not-allowed" : "pointer",
          opacity: pageIdx <= 0 ? 0.35 : 1,
        }}
      >
        <img src="/v2/left.png" alt="Prev" style={{ width: 34, height: 52 }} />
      </button>

      <button
        type="button"
        onClick={() => scrollToIndex(pageIdx + 1)}
        disabled={pageIdx >= arenasList.length - 1}
        style={{
          position: "absolute",
          top: "25%",
          right: 10,
          zIndex: 60,
          width: 34,
          height: 52,
          background: "transparent",
          border: 0,
          display: "grid",
          placeItems: "center",
          cursor: pageIdx >= arenasList.length - 1 ? "not-allowed" : "pointer",
          opacity: pageIdx >= arenasList.length - 1 ? 0.35 : 1,
        }}
      >
        <img src="/v2/right.png" alt="Next" style={{ width: 34, height: 52 }} />
      </button>


      <div
        ref={scrollerRef}
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          touchAction: "pan-x",
        }}
      >
        {arenasList.length === 0 ? (
          <div
            style={{
              flex: "0 0 100%",
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              textShadow: "1px 1px #000",
              paddingTop: 120,
              boxSizing: "border-box",
            }}
          >
            No Arenas Yet
          </div>
        ) : (
          arenasList.map((a, i) => {

            const unopenedCount = Number(unopenedChestCountByArena[String(a?.id)] || 0);
            const hasUnopenedChests = unopenedCount > 0;

            const globalIdx = arenasList.findIndex((x) => x?.id === a?.id);

            const max = toNum(a?.trophyMax, Infinity);

            const isPastArena = trophies > max;
            const owned = ownsArenaEntry(a?.id);
            const paid = isPaidArena(a);

            const isBelowEffective = protectionActive && currentIdx >= 0 && i < currentIdx;
            const isEffectiveArena = currentIdx >= 0 && i === currentIdx;

            const trophyOk = isEffectiveArena ? true : canEnterByTrophies(a);

            //const enterable = !isBelowEffective && (trophyOk || isPastArena) && (!paid || owned);
            const canPlay = !isBelowEffective && !isPastArena && trophyOk && (!paid || owned);
            const canView = (trophyOk || isPastArena || isBelowEffective) && (!paid || owned);

            const need = neededForMin(a);
            


            let lockedReason = null;

if (isBelowEffective) {
  lockedReason = "Arena floor protection active";
} else if (!trophyOk) {
  lockedReason = need > 0 ? `Need ${need} trophies` : "Completed";
} else if (paid && !owned && !hasGamepass) {
  lockedReason = "Purchase required";
}


            const priceLabel = getArenaPriceLabel(a);

            return (
              <div
                key={a?.id ?? `${a?.name}-${i}`}
                style={{
                  flex: "0 0 100%",
                  width: "100%",
                  height: "100%",
                  scrollSnapAlign: "start",
                  position: "relative",
                  boxSizing: "border-box",
                }}
              >
                {/* Big arena image */}


                <div style={{borderRadius: 10, backgroundImage: 'url("/v2/main_bg.png")',backgroundRepeat: "repeat",backgroundSize: "100%",backgroundPosition: "left top",overflow: "hidden", position: "relative", marginBottom: 20, backgroundColor: "#fff", float: "left",width: "100%",height: "calc(100% - 115px)"}}>

                {!trophyOk && !isPastArena && (
                  <img
                    src="/v2/lock.png"
                    style={{
                      zIndex: 5,
                      left: "calc(50% - 40px)",
                      top: "25%",
                      position: "absolute",
                      width: 80,
                    }}
                    alt=""
                  />
                )}
                
                <div
                  style={{
                    opacity: !trophyOk && !isPastArena ? 0.5 : 1,
                    width: "100%",
                    height: "100%",
                    background: `url(v2/${a?.imageUrl || "/arena.png"}) center bottom 65% / 65% auto no-repeat`,
                  }}
                >
                
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      zIndex: 3,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      borderRadius: 6,
                      background: "#54240f",
                      color: "#FFE100",
                      fontWeight: 600,
                      fontSize: 16,
                      textShadow: "1px 1px #000",
                      fontFamily: '"Fredoka", sans-serif',
                    }}
                  >
                    <img src="/v2/trophy.png" alt="" style={{ width: 18, height: 18 }} />
                    <span>
                      {trophies}
                    </span>
                  </div>



                  {/* ✅ Title row: name LEFT, price RIGHT */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 5,
                      left: 5,
                      width: "calc(100% - 10px)",
                      color: "#fff",
                      textShadow: "2px 2px #000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      backgroundColor: "#723e1d",
                      border: "1px solid #632712",
                      borderBottom: "5px solid #632712",
                      borderRadius: 8,
                      padding: "5px 10px",
                      paddingRight: 4,
                      boxSizing: "border-box",
                      fontFamily: '"Fredoka", sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                        letterSpacing: 0.6,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "70%",
                      }}
                    >
                      {a?.name ?? "Arena"}
                    </div>

                    

                    {hasUnopenedChests && (
  <div
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();

      enterArena(a, { viewOnly: true });
    }}
    style={{
      fontSize: 12,
      lineHeight: "22px",
      borderRadius: "50%",
      color: "#fff",
      position: "absolute",
      top: -10,
      right: -3,
      height: 22,
      width: 22,
      backgroundColor: "#fe4300",
      borderBottom: "1px solid #ca0600",
      display: "grid",
      placeItems: "center",
      fontWeight: 600,
      zIndex: 5,
      cursor: "pointer",
    }}
  >
    {unopenedCount > 9 ? "9+" : unopenedCount}
  </div>
)}

                    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                    {/* Required trophies range (MIN / MAX) */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 14,
                        color: "#FFE100",
                        fontWeight: 600,
                        padding: "6px 10px",
                        paddingLeft: 0,
                        borderRadius: 12,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <img src="/v2/trophy.png" alt="" style={{ width: 18, height: 18 }} />
                      <span>
                        {toNum(a?.trophyMin, 0)}/{toNum(a?.trophyMax, 0)}
                      </span>
                    </div>



                    {/* Price (unchanged) */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 16,
                        fontWeight: 600,
                        padding: "6px 10px",
                        borderRadius: 6,
                        background: "linear-gradient(to bottom, #7dc84e 0%, #7dc84e 45%, #6abb38 45%, #6abb38 100%)",
                        whiteSpace: "nowrap",
                        textShadow: "1px 1px #127b05",
                        fontSize: "14px"
                      }}
                    >
                      {a?.entryFee?.amount > 0 && a?.entryFee?.currency !== "free" ? (
                        <>
                          <img
                            src={
                              a.entryFee.currency === "runes"
                                ? "/runes.png"
                                : a.entryFee.currency === "sparks"
                                ? "/v2/sparks.png"
                                : null
                            }
                            alt=""
                            style={{ width: 18, height: 18 }}
                          />
                          <span>{a.entryFee.amount}</span>
                        </>
                      ) : (
                        <span>FREE</span>
                      )}
                    </div>
                  </div>

                  </div>
                  </div>

                  {/* Lock icon */}
                  <div style={{ position: "absolute", top: 10, right: 10 }}>
                    <img
  src={canPlay || isBelowEffective || isPastArena ? "/v2/check.png" : "/v2/lock.png"}
  style={
    canPlay || isBelowEffective || isPastArena
      ? { width: 24, height: 24 }
      : { width: 24, height: 31 }
  }
/>
                  </div>
                </div>

                {/* Bottom panel */}
                <div style={{ padding: 16 }}>
                                    <button
                    type="button"
                    disabled={globalIdx < 0 || purchaseBusy || (!canView && !paid)}
                    onClick={() => {
                      if (globalIdx < 0) return;

                      const max = toNum(a?.trophyMax, Infinity);
                      const isPastArena = trophies > max;
                      const viewOnlyForThisArena = isPastArena || isBelowEffective;

                      //if (isBelowEffective) return;
                      if (!trophyOk && !isPastArena) return;

                      const paid = isPaidArena(a);
                      const owned = isArenaOwned(a);

                      if (!paid || owned) {
                        //enterArena(a, { viewOnly: isPastArena });
                        enterArena(a, { viewOnly: viewOnlyForThisArena });
                        return;
                      }

                      setPendingArena(a);
                      setShowConfirmModal(true);
                    }}
                    className={[
  "game-button",
  (!trophyOk && !isPastArena) ? "red-button" : "green-button",
  purchaseBusy ? "game-button-disabled" : ""
].join(" ")}
                  >
                    {purchaseBusy
  ? "PROCESSING..."
 : (!trophyOk && !isPastArena)
? "LOCKED ARENA"
: isBelowEffective
? "VIEW ARENA"
: isPastArena
? "VIEW ARENA"
: paid && !owned
? `BUY (${getArenaPriceLabel(a)})`
: "ENTER THE BATTLE"}


                  </button>


                  {/* Small pager hint */}
                  <div
                    style={{
                      fontFamily: '"Fredoka", sans-serif',
                      fontWeight: "bold",
                      marginTop: 15,
                      textAlign: "center",
                      color: "#fff",
                      textShadow: "1px 1px #000",
                    }}
                  >
                    Swipe Left / Right To See More Arenas
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

                          {showConfirmModal && pendingArena && !showErrorModal && !showSuccessModal && (
        <div className="modal_box">
        <div className="modal_title">Confirm? <button onClick={() => {
                  setShowConfirmModal(false);
                  setPendingArena(null);
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Confirm"
              style={{ height: "80px", marginBottom: "10px" }}
            />
            <div className="shadow2" style={{ fontWeight: 600, fontSize: 24, color: "#fff", marginBottom: "10px" }}>
              Confirm Entry Purchase?
            </div>

            <p className="info"
              style={{
                fontSize: "21px",
                color: "#ddd",
              }}
            >
              Buy access to{" "}
              <span style={{ color: "#09BDFF", fontWeight: "bold" }}>
                {pendingArena?.name ?? "Arena"}
              </span>{" "}
              for{" "}
              <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
                {getArenaPriceLabel(pendingArena)}
              </span>
              ?
            </p>
            </div>

            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}}
                onClick={async () => {
                  try {
                    setPurchaseBusy(true);

                   const arenaId = pendingArena?.id;
                    const result = await purchaseArenaEntry(arenaId);

                    if (result?.balances) {
                      setUser?.(prev => ({
                        ...prev,
                        runes: result.balances.runes,
                        sparks: result.balances.sparks,
                      }));
                    }

                    await refreshMe();

                    setOwnedArenaIds(prev => {
                      const next = new Set(prev);
                      next.add(String(arenaId));
                      return next;
                    });

                    setShowConfirmModal(false);
                    setShowSuccessModal(true);


                  } catch (err) {
                    setShowConfirmModal(false);
                    setErrorMessage(err?.message || "Purchase failed");
                    setShowErrorModal(true);
                  } finally {
                    setPurchaseBusy(false);
                  }
                }}
                disabled={purchaseBusy}
              >
                {purchaseBusy ? "Buying..." : "Confirm"}
              </button>

              <button className="game-button red-button"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingArena(null);
                }}
                disabled={purchaseBusy}
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}

            {showSuccessModal && pendingArena && (
        <div className="modal_box">
        <div className="modal_title">Success<button onClick={() => {
                  setShowSuccessModal(false);
                  setPendingArena(null);
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Success"
              style={{ height: "80px", marginBottom: "10px" }}
            />
            <div className="shadow2" style={{ color: "#fff", fontWeight: 600, fontSize: 24, marginBottom: "10px" }}>
              Transaction Completed!
            </div>

            <p className="info"
              style={{
                fontSize: 16,
                color: "#ddd",
              }}
            >
              You unlocked{" "}
              <span style={{ color: "#09BDFF", fontWeight: "bold" }}>
                {pendingArena?.name ?? "Arena"}
              </span>{" "}
              🎉
            </p>
            </div>
            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}}
                onClick={() => {
                  setShowSuccessModal(false);
                  const a = pendingArena;
                  setPendingArena(null);
                  const max = toNum(a?.trophyMax, Infinity);
                  enterArena(a, { viewOnly: trophies > max });
                }}
              >
                Enter Now
              </button>

              <button className="game-button red-button"
                onClick={() => {
                  setShowSuccessModal(false);
                  setPendingArena(null);
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showErrorModal && (
        <div className="modal_box">
        <div className="modal_title">Error<button onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
          
          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: "80px",
                marginBottom: "10px",
                transform: "rotate(180deg)",
              }}
            />
            <div className="shadow2" style={{ fontSize: 21,color: "#fff", fontWeight: 600}}>
              Transaction Failed
            </div>

            <p className="info"
              style={{
                fontSize: "16px",
                color: "#ddd",
              }}
            >
              {errorMessage}
            </p>
            </div>

            <div className="modal_footer">
              <button
                className="game-button green-button" style={{marginRight: 10}}
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                  onNavigate?.("shop", { initialView: "coin_2" }); // same idea as Shop: go buy runes
                }}
              >
                Buy Runes
              </button>
              <button className="game-button red-button"
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}


    </div>
  );
}
